import { useMemo } from "react";

import axiosInstance from "src/utils/axios";
import { authenticatedSwrOptions } from "src/utils/constants";

import useAuthenticatedSWR from "src/auth/hooks/use-authenticated-swr";

import { GeographicEndpoints } from "./geographicEndpoints";

// Continent
//----------------------------------------------------------------------
export function useGetContinents(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Continent.getAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        continents: data || [],
        continentsLoading: isLoading,
        continentsError: error,
        continentsValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetContinent(id) {
    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Continent.getById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        continent: data || {},
        continentLoading: isLoading,
        continentError: error,
        continentValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useContinentOperations() {
    const createContinent = async (data) => {
        try {
            const response = await axiosInstance.post(
                GeographicEndpoints.Continent.create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating continent:', error);
            throw error;
        }
    };

    const updateContinent = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                GeographicEndpoints.Continent.update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating continent:', error);
            throw error;
        }
    };

    const deleteContinent = async (id) => {
        try {
            const response = await axiosInstance.delete(
                GeographicEndpoints.Continent.delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting continent:', error);
            throw error;
        }
    };

    return { createContinent, updateContinent, deleteContinent };
}

// Country
//----------------------------------------------------------------------

export function useGetCountries(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Country.getAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        countries: data || [],
        countriesLoading: isLoading,
        countriesError: error,
        countriesValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetCountry(id) {
    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Country.getById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        country: data || {},
        countryLoading: isLoading,
        countryError: error,
        countryValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useCountryOperations() {
    const createCountry = async (data) => {
        try {
            const response = await axiosInstance.post(
                GeographicEndpoints.Country.create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating country:', error);
            throw error;
        }
    };

    const updateCountry = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                GeographicEndpoints.Country.update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating country:', error);
            throw error;
        }
    };

    const deleteCountry = async (id) => {
        try {
            const response = await axiosInstance.delete(
                GeographicEndpoints.Country.delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting country:', error);
            throw error;
        }
    };

    return { createCountry, updateCountry, deleteCountry };
}
// District
//----------------------------------------------------------------------

export function useGetDistricts(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.District.getAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        districts: data || [],
        districtsLoading: isLoading,
        districtsError: error,
        districtsValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetDistrict(id) {
    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.District.getById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        district: data || {},
        districtLoading: isLoading,
        districtError: error,
        districtValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useDistrictOperations() {
    const createDistrict = async (data) => {
        try {
            const response = await axiosInstance.post(
                GeographicEndpoints.District.create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating district:', error);
            throw error;
        }
    };

    const updateDistrict = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                GeographicEndpoints.District.update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating district:', error);
            throw error;
        }
    };

    const deleteDistrict = async (id) => {
        try {
            const response = await axiosInstance.delete(
                GeographicEndpoints.District.delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting district:', error);
            throw error;
        }
    };

    return { createDistrict, updateDistrict, deleteDistrict };
}

// Provience
//----------------------------------------------------------------------

export function useGetProvinces(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Provience.getAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        provinces: data || [],
        provincesLoading: isLoading,
        provincesError: error,
        provincesValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetProvince(id) {
    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Provience.getById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        province: data || {},
        provinceLoading: isLoading,
        provinceError: error,
        provinceValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useProvinceOperations() {
    const createProvince = async (data) => {
        try {
            const response = await axiosInstance.post(
                GeographicEndpoints.Provience.create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating province:', error);
            throw error;
        }
    };

    const updateProvince = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                GeographicEndpoints.Provience.update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating province:', error);
            throw error;
        }
    };

    const deleteProvince = async (id) => {
        try {
            const response = await axiosInstance.delete(
                GeographicEndpoints.Provience.delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting province:', error);
            throw error;
        }
    };

    return { createProvince, updateProvince, deleteProvince };
}

// Region
//----------------------------------------------------------------------

export function useGetRegions(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Region.getAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        regions: data || [],
        regionsLoading: isLoading,
        regionsError: error,
        regionsValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useFilteredRegions() {
    const fetchRegions = async ({ CountryId = 0, Name = "", SortId = 0 }) => {
        try {
            const response = await axiosInstance.post(
                GeographicEndpoints.Region.getAllWithFilter,
                {
                    SortId,
                    Name,
                    CountryId
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching filtered regions:', error);
            throw error;
        }
    };

    return { fetchRegions };
}

export function useGetRegion(id) {
    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(GeographicEndpoints.Region.getById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        region: data || {},
        regionLoading: isLoading,
        regionError: error,
        regionValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useRegionOperations() {
    const createRegion = async (data) => {
        try {
            const response = await axiosInstance.post(
                GeographicEndpoints.Region.create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating region:', error);
            throw error;
        }
    };

    const updateRegion = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                GeographicEndpoints.Region.update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating region:', error);
            throw error;
        }
    };

    const deleteRegion = async (id) => {
        try {
            const response = await axiosInstance.delete(
                GeographicEndpoints.Region.delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting region:', error);
            throw error;
        }
    };

    return { createRegion, updateRegion, deleteRegion };
}